/**
 * ============================================================================
 * MOCK DATA — THE ONLY FILE IN THIS APP THAT INVENTS ANYTHING.
 * ============================================================================
 *
 * Six of this panel's screens need endpoints the server does not have yet. The
 * contract is frozen, so those endpoints are additive work behind an ADR rather
 * than something this track can add. Until they land, every read they need is
 * served from here.
 *
 * Rules that keep this honest:
 *
 *   - Nothing outside `client.ts` imports this module. A screen cannot tell
 *     whether it is looking at mock or live data, so no screen can grow a
 *     dependency on mock shapes.
 *   - Every mock is typed with the SAME type the live call returns, so swapping
 *     is deleting a branch, not rewriting a component.
 *   - `VITE_ADMIN_MOCK=0` (the production default) sends every call live.
 *     Mock mode exists only so `pnpm dev` runs without a server; the header
 *     chip and sidebar say Mock whenever it is on.
 *
 * Figures below are plausible, not real. They are shaped to exercise the UI:
 * one game drifting well off its configured edge, one payment method near its
 * cap, one degraded method, one suspended player.
 */
import type {
  AuditEntry,
  DailyMetric,
  DepositFunnel,
  PlayerLifetime,
  ReconciliationReport,
  RetentionCohort,
  SecretView,
  AdminDeposit,
  AdminGame,
  AdminPaymentMethod,
  AdminWithdrawal,
  ApproveOutcome,
  PaymentMethodOptions,
  GameHold,
  LedgerEntry,
  MetricsWindow,
  Overview,
  PlatformSetting,
  PlayerSummary,
  WithdrawalView,
} from './types.ts';

/** Master switch. `VITE_ADMIN_MOCK=0` sends everything at the real API. */
export const MOCK_MODE = (import.meta.env['VITE_ADMIN_MOCK'] as string | undefined) !== '0';

/* The MISSING_ENDPOINTS list is gone because nothing is missing any more:
 * T-028 landed all eleven /internal/admin/* routes (server/src/admin/routes.ts)
 * and T-029 landed operator auth. Mock mode survives only so `pnpm dev`
 * renders every screen without a server — production runs VITE_ADMIN_MOCK=0. */

/**
 * The key manager (T-143) is LIVE from the day it shipped. Its mock below
 * exists only so `pnpm dev` renders the screen
 * without a server — and it deliberately mocks the READS only. **Setting a key
 * is never mocked**: a form that appears to store a credential and does not is
 * the worst possible thing for this particular screen to fake, so in mock mode
 * the write calls the real endpoint and fails honestly.
 */

/** Endpoints that DO exist and are called for real whenever mock mode is off. */
export const LIVE_ENDPOINTS: readonly string[] = [
  'POST /internal/payments/deposits/confirm',
  'POST /internal/payments/deposits/expire',
  'POST /internal/payments/withdrawals/:id/approve',
  'POST /internal/payments/withdrawals/:id/paid',
  'POST /internal/payments/withdrawals/:id/reject',
  'GET /internal/admin/secrets',
  'GET /internal/admin/secrets/:name/history',
  'POST /internal/admin/secrets/:name',
  'POST /internal/admin/secrets/:name/check',
  'POST /internal/admin/secrets/:name/revoke',
];

const now = Date.now();
const iso = (minutesAgo: number): string => new Date(now - minutesAgo * 60000).toISOString();
const future = (minutes: number): string => new Date(now + minutes * 60000).toISOString();

/** Latency, so loading states are exercised rather than assumed. */
export function settle<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => window.setTimeout(() => resolve(structuredClone(value)), ms));
}

// ------------------------------------------------------------------- players

export const players: PlayerSummary[] = [
  {
    userId: 1042,
    username: 'rahul_87',
    email: 'rahul87@example.com',
    phone: '+919812345601',
    status: 'active',
    currency: 'INR',
    balance: '18450.5000',
    depositedTotal: '124000.0000',
    withdrawnTotal: '86500.0000',
    turnover: '1842300.0000',
    netPosition: '-19049.5000',
    lastActiveAt: iso(4),
    createdAt: iso(60 * 24 * 212),
  },
  {
    userId: 1188,
    username: 'meena_p',
    email: 'meena.p@example.com',
    phone: '+919812345602',
    status: 'active',
    currency: 'INR',
    balance: '2310.7500',
    depositedTotal: '31000.0000',
    withdrawnTotal: '12000.0000',
    turnover: '284900.0000',
    netPosition: '-16689.2500',
    lastActiveAt: iso(23),
    createdAt: iso(60 * 24 * 96),
  },
  {
    userId: 1301,
    username: 'kabir_x',
    email: null,
    phone: '+919812345603',
    status: 'active',
    currency: 'INR',
    balance: '96820.0000',
    depositedTotal: '48000.0000',
    withdrawnTotal: '15000.0000',
    turnover: '3120400.0000',
    netPosition: '63820.0000',
    lastActiveAt: iso(1),
    createdAt: iso(60 * 24 * 31),
  },
  {
    userId: 1344,
    username: 'anita_r',
    email: 'anita.r@example.com',
    phone: '+919812345604',
    status: 'suspended',
    currency: 'INR',
    balance: '7400.0000',
    depositedTotal: '52000.0000',
    withdrawnTotal: '61000.0000',
    turnover: '740200.0000',
    netPosition: '16400.0000',
    lastActiveAt: iso(60 * 30),
    createdAt: iso(60 * 24 * 140),
  },
  {
    userId: 1402,
    username: 'devs_09',
    email: 'devs09@example.com',
    phone: '+919812345605',
    status: 'active',
    currency: 'INR',
    balance: '512.2500',
    depositedTotal: '9000.0000',
    withdrawnTotal: '0.0000',
    turnover: '61200.0000',
    netPosition: '-8487.7500',
    lastActiveAt: iso(140),
    createdAt: iso(60 * 24 * 9),
  },
  {
    userId: 1455,
    username: 'sunny_bet',
    email: null,
    phone: '+919812345606',
    status: 'closed',
    currency: 'INR',
    balance: '0.0000',
    depositedTotal: '18000.0000',
    withdrawnTotal: '18000.0000',
    turnover: '240000.0000',
    netPosition: '0.0000',
    lastActiveAt: iso(60 * 24 * 44),
    createdAt: iso(60 * 24 * 300),
  },
];

const statementsByUser: Record<number, LedgerEntry[]> = {
  1042: [
    { id: 90412, amount: '-250.0000', kind: 'bet', refType: 'bet', refId: 88431, currency: 'INR', balanceAfter: '18450.5000', createdAt: iso(4) },
    { id: 90411, amount: '480.0000', kind: 'win', refType: 'bet', refId: 88430, currency: 'INR', balanceAfter: '18700.5000', createdAt: iso(5) },
    { id: 90410, amount: '-240.0000', kind: 'bet', refType: 'bet', refId: 88430, currency: 'INR', balanceAfter: '18220.5000', createdAt: iso(5) },
    { id: 90402, amount: '10000.0000', kind: 'deposit', refType: 'deposit', refId: 5521, currency: 'INR', balanceAfter: '18460.5000', createdAt: iso(190) },
    { id: 90388, amount: '-5000.0000', kind: 'withdrawal', refType: 'withdrawal', refId: 771, currency: 'INR', balanceAfter: '8460.5000', createdAt: iso(1450) },
  ],
  1301: [
    { id: 90520, amount: '3200.0000', kind: 'win', refType: 'bet', refId: 88510, currency: 'INR', balanceAfter: '96820.0000', createdAt: iso(1) },
    { id: 90519, amount: '-800.0000', kind: 'bet', refType: 'bet', refId: 88510, currency: 'INR', balanceAfter: '93620.0000', createdAt: iso(1) },
    { id: 90518, amount: '-800.0000', kind: 'bet', refType: 'bet', refId: 88509, currency: 'INR', balanceAfter: '94420.0000', createdAt: iso(2) },
    { id: 90517, amount: '2400.0000', kind: 'win', refType: 'bet', refId: 88508, currency: 'INR', balanceAfter: '95220.0000', createdAt: iso(3) },
    { id: 90502, amount: '20000.0000', kind: 'deposit', refType: 'deposit', refId: 5498, currency: 'INR', balanceAfter: '92820.0000', createdAt: iso(600) },
  ],
};

const genericStatement: LedgerEntry[] = [
  { id: 80003, amount: '-100.0000', kind: 'bet', refType: 'bet', refId: 70003, currency: 'INR', balanceAfter: '2310.7500', createdAt: iso(23) },
  { id: 80002, amount: '190.0000', kind: 'win', refType: 'bet', refId: 70002, currency: 'INR', balanceAfter: '2410.7500', createdAt: iso(24) },
  { id: 80001, amount: '-100.0000', kind: 'bet', refType: 'bet', refId: 70002, currency: 'INR', balanceAfter: '2220.7500', createdAt: iso(24) },
  { id: 80000, amount: '2000.0000', kind: 'deposit', refType: 'deposit', refId: 5410, currency: 'INR', balanceAfter: '2320.7500', createdAt: iso(400) },
];

export function statementFor(userId: number): LedgerEntry[] {
  return statementsByUser[userId] ?? genericStatement;
}

export function playerById(userId: number): PlayerSummary | undefined {
  return players.find((p) => p.userId === userId);
}

// ------------------------------------------------------------------ deposits

export const deposits: AdminDeposit[] = [
  {
    depositId: 5610, userId: 1042, username: 'rahul_87', amount: '5000.0000', kind: 'upi',
    status: 'pending', reference: 'DEP7K2M9QX4A', utrReference: null,
    claimedUtr: '418923117755', utrClaimedAt: iso(2), claimedUtrDuplicate: false, methodId: 3,
    methodLabel: 'UPI · Axis 4412', methodOwnerEntity: 'Northwind Retail Pvt Ltd',
    address: 'northwind4412@axisbank', expiresAt: future(11), creditedAt: null, createdAt: iso(9),
  },
  {
    depositId: 5609, userId: 1188, username: 'meena_p', amount: '1500.0000', kind: 'upi',
    status: 'pending', reference: 'DEP3B8N1TR7C', utrReference: null,
    claimedUtr: null, utrClaimedAt: null, claimedUtrDuplicate: false, methodId: 1,
    methodLabel: 'UPI · HDFC 9021', methodOwnerEntity: 'Northwind Retail Pvt Ltd',
    address: 'northwind9021@hdfcbank', expiresAt: future(3), creditedAt: null, createdAt: iso(17),
  },
  {
    depositId: 5608, userId: 1402, username: 'devs_09', amount: '20000.0000', kind: 'bank',
    status: 'pending', reference: 'DEP9V4L2ZK8D', utrReference: null,
    claimedUtr: null, utrClaimedAt: null, claimedUtrDuplicate: false, methodId: 4,
    methodLabel: 'Bank · ICICI 7730', methodOwnerEntity: 'Northwind Retail Pvt Ltd',
    address: 'ICIC0000773 / 7730114526', expiresAt: future(28), creditedAt: null, createdAt: iso(19),
  },
  {
    depositId: 5607, userId: 1301, username: 'kabir_x', amount: '75000.0000', kind: 'crypto',
    status: 'pending', reference: 'DEP5H6Q3WJ1E', utrReference: null,
    claimedUtr: null, utrClaimedAt: null, claimedUtrDuplicate: false, methodId: 5,
    methodLabel: 'Crypto · USDT-TRC20', methodOwnerEntity: 'Northwind Digital FZ-LLC',
    address: 'TQn9Y2khE1s4mF7pV3bXwLd8cRa6TjZu2K', expiresAt: future(64), creditedAt: null, createdAt: iso(26),
  },
  {
    depositId: 5606, userId: 1188, username: 'meena_p', amount: '2500.0000', kind: 'upi',
    status: 'credited', reference: 'DEP2C7X5PM3F', utrReference: '418923004471',
    claimedUtr: '418923004471', utrClaimedAt: iso(52), claimedUtrDuplicate: false, methodId: 3,
    methodLabel: 'UPI · Axis 4412', methodOwnerEntity: 'Northwind Retail Pvt Ltd',
    address: 'northwind4412@axisbank', expiresAt: iso(30), creditedAt: iso(48), createdAt: iso(55),
  },
  {
    depositId: 5605, userId: 1042, username: 'rahul_87', amount: '10000.0000', kind: 'upi',
    status: 'credited', reference: 'DEP8T1D6YB9G', utrReference: '418922991003',
    claimedUtr: null, utrClaimedAt: null, claimedUtrDuplicate: false, methodId: 1,
    methodLabel: 'UPI · HDFC 9021', methodOwnerEntity: 'Northwind Retail Pvt Ltd',
    address: 'northwind9021@hdfcbank', expiresAt: iso(170), creditedAt: iso(190), createdAt: iso(196),
  },
  {
    depositId: 5604, userId: 1455, username: 'sunny_bet', amount: '3000.0000', kind: 'upi',
    status: 'expired', reference: 'DEP4R9F2GH5J', utrReference: null,
    claimedUtr: null, utrClaimedAt: null, claimedUtrDuplicate: false, methodId: 3,
    methodLabel: 'UPI · Axis 4412', methodOwnerEntity: 'Northwind Retail Pvt Ltd',
    address: 'northwind4412@axisbank', expiresAt: iso(240), creditedAt: null, createdAt: iso(260),
  },
];

// --------------------------------------------------------------- withdrawals

export const withdrawals: AdminWithdrawal[] = [
  {
    withdrawalId: 812, userId: 1301, username: 'kabir_x', currency: 'INR', amount: '45000.0000', fee: '0.0000',
    netAmount: '45000.0000', status: 'requested',
    destination: { kind: 'upi', region: 'IN', vpa: 'kabir.x@okhdfcbank', name: 'Kabir Sharma' },
    region: 'IN',
    // Above the four-eyes threshold (ADR-045), first signature already cast by
    // a DIFFERENT operator (#991) — this row is "1 of 2" and waits for a second.
    reviewedBy: null, firstApprovedBy: 991, firstApprovedAt: iso(6), reviewNote: null, paidAt: null, createdAt: iso(12),
  },
  {
    withdrawalId: 811, userId: 1042, username: 'rahul_87', currency: 'INR', amount: '5000.0000', fee: '0.0000',
    netAmount: '5000.0000', status: 'requested',
    destination: { kind: 'bank', region: 'IN', accountNumber: '50100294417', ifsc: 'HDFC0001234', name: 'Rahul Verma' },
    region: 'IN',
    reviewedBy: null, firstApprovedBy: null, firstApprovedAt: null, reviewNote: null, paidAt: null, createdAt: iso(38),
  },
  {
    withdrawalId: 810, userId: 1344, username: 'anita_r', currency: 'INR', amount: '12000.0000', fee: '0.0000',
    netAmount: '12000.0000', status: 'requested',
    destination: { kind: 'upi', region: 'IN', vpa: 'anita.r@ybl' },
    region: 'IN',
    reviewedBy: null, firstApprovedBy: null, firstApprovedAt: null, reviewNote: null, paidAt: null, createdAt: iso(96),
  },
  {
    withdrawalId: 809, userId: 1188, username: 'meena_p', currency: 'INR', amount: '8000.0000', fee: '0.0000',
    netAmount: '8000.0000', status: 'approved',
    destination: { kind: 'upi', region: 'IN', vpa: 'meena.p@okaxis', name: 'Meena Patel' },
    region: 'IN',
    reviewedBy: 0, firstApprovedBy: null, firstApprovedAt: null, reviewNote: 'KYC on file, first payout.', paidAt: null, createdAt: iso(150),
  },
  {
    withdrawalId: 808, userId: 1042, username: 'rahul_87', currency: 'INR', amount: '15000.0000', fee: '0.0000',
    netAmount: '15000.0000', status: 'paid',
    destination: { kind: 'crypto', region: 'GLOBAL', network: 'TRON', address: 'TWd4Yn8bK2xLm5QcV7pRz1aJf6Hu3TgS9e' },
    region: 'GLOBAL',
    reviewedBy: 0, firstApprovedBy: 991, firstApprovedAt: iso(770), reviewNote: null, paidAt: iso(700), createdAt: iso(760),
  },
  {
    withdrawalId: 807, userId: 1455, username: 'sunny_bet', currency: 'INR', amount: '6000.0000', fee: '0.0000',
    netAmount: '6000.0000', status: 'rejected',
    destination: { kind: 'upi', region: 'IN', vpa: 'sunny.bet@ybl' },
    region: 'IN',
    reviewedBy: 0, firstApprovedBy: null, firstApprovedAt: null, reviewNote: 'Destination VPA does not match the account holder.',
    paidAt: null, createdAt: iso(1600),
  },
];

/** The platform policy store (ADR-045) for mock mode — a ₹10,000 four-eyes floor. */
export const settings: PlatformSetting[] = [
  { key: 'payout_policy', value: { fourEyesAboveInr: 10000 }, updatedBy: 0, updatedAt: iso(2880) },
  { key: 'operator_mfa', value: { graceOpen: true }, updatedBy: 0, updatedAt: iso(2880) },
];

/** Mock a settings write. */
export function updateSettingMock(key: string, value: unknown, updatedBy: number): PlatformSetting {
  const existing = settings.find((s) => s.key === key);
  const row = { key, value, updatedBy, updatedAt: new Date().toISOString() };
  if (existing) Object.assign(existing, row);
  else settings.push(row);
  return row;
}

/** The mock four-eyes threshold, so the fixture and the panel agree. */
function mockFourEyesThresholdInr(): number {
  const policy = settings.find((s) => s.key === 'payout_policy')?.value as { fourEyesAboveInr?: number } | undefined;
  return policy?.fourEyesAboveInr ?? Number.POSITIVE_INFINITY;
}

/**
 * Mock the two-phase approve (ADR-045): below the threshold one approval
 * transitions; at/above it the first signature stamps and leaves `requested`,
 * the same operator twice throws, a different operator completes.
 */
export function approveMock(
  id: number,
  operatorId: number,
  note?: string,
): ApproveOutcome {
  const row = withdrawals.find((w) => w.withdrawalId === id);
  if (!row) throw new Error('Withdrawal not found.');

  const needsTwo = Number(row.amount) >= mockFourEyesThresholdInr();

  if (needsTwo && row.status === 'requested') {
    if (row.firstApprovedBy === null) {
      row.firstApprovedBy = operatorId;
      row.firstApprovedAt = new Date().toISOString();
      return { ...toView(row), firstSignature: true, approversRequired: 2, firstApprovedBy: operatorId };
    }
    if (row.firstApprovedBy === operatorId) {
      throw new Error('This payout needs a second, different approver.');
    }
    row.status = 'approved';
    row.reviewedBy = operatorId;
    if (note !== undefined) row.reviewNote = note;
    return { ...toView(row), firstSignature: false, approversRequired: 2, firstApprovedBy: row.firstApprovedBy };
  }

  row.status = 'approved';
  row.reviewedBy = operatorId;
  if (note !== undefined) row.reviewNote = note;
  return { ...toView(row), firstSignature: false, approversRequired: 1, firstApprovedBy: row.firstApprovedBy };
}

function toView(row: AdminWithdrawal): WithdrawalView {
  return {
    withdrawalId: row.withdrawalId,
    amount: row.amount,
    fee: row.fee,
    netAmount: row.netAmount,
    status: row.status,
    destination: row.destination,
    reviewNote: row.reviewNote,
    paidAt: row.paidAt,
    createdAt: row.createdAt,
  };
}

// --------------------------------------------------------------------- games

/**
 * `crash` is deliberately drifting: 4.31% realised against a 3.00% configured
 * edge on high turnover. That is the shape of the signal the dashboard exists
 * to surface — and `mines` drifting the other way, at 0.86% against 2.50%, is
 * the shape that costs money.
 */
export const games: AdminGame[] = [
  { id: 1, slug: 'dragon-tiger', name: 'Dragon vs Tiger', kind: 'round', enabled: true, minStake: '10.0000', maxStake: '50000.0000', maxPayout: '200000.0000', targetEdge: '0.0216', payoutTable: { dragon: '1.9500', tiger: '1.9500', tie: '8.0000' } },
  { id: 2, slug: 'crash', name: 'Crash', kind: 'round', enabled: true, minStake: '10.0000', maxStake: '100000.0000', maxPayout: '200000.0000', targetEdge: '0.0300', payoutTable: { model: 'inverse-uniform', houseEdge: '0.0300', maxMultiplier: '10000.0000' } },
  { id: 3, slug: 'mines', name: 'Mines', kind: 'instant', enabled: true, minStake: '10.0000', maxStake: '25000.0000', maxPayout: '200000.0000', targetEdge: '0.0250', payoutTable: { grid: 25, model: 'hypergeometric', houseEdge: '0.0250' } },
  { id: 4, slug: 'andar-bahar', name: 'Andar Bahar', kind: 'round', enabled: true, minStake: '10.0000', maxStake: '50000.0000', maxPayout: '200000.0000', targetEdge: '0.0246', payoutTable: { andar: '1.9000', bahar: '2.0000' } },
  { id: 5, slug: 'plinko', name: 'Plinko', kind: 'instant', enabled: true, minStake: '10.0000', maxStake: '20000.0000', maxPayout: '200000.0000', targetEdge: '0.0200', payoutTable: { rows: 16, risk: ['low', 'medium', 'high'] } },
  { id: 6, slug: 'european-roulette', name: 'European Roulette', kind: 'table', enabled: true, minStake: '10.0000', maxStake: '80000.0000', maxPayout: '200000.0000', targetEdge: '0.0270', payoutTable: { straight: '36.0000', split: '18.0000', red: '2.0000' } },
  { id: 7, slug: 'teen-patti', name: 'Teen Patti (dealer)', kind: 'table', enabled: false, minStake: '20.0000', maxStake: '40000.0000', maxPayout: '200000.0000', targetEdge: '0.0225', payoutTable: { play: '2.0000', ante: '1.0000' } },
  { id: 8, slug: 'keno', name: 'Keno', kind: 'round', enabled: true, minStake: '10.0000', maxStake: '10000.0000', maxPayout: '200000.0000', targetEdge: '0.0400', payoutTable: { spots: 10, model: 'paytable-v2' } },
];

export const gameHold: GameHold[] = [
  { slug: 'crash', name: 'Crash', enabled: true, kind: 'round', turnover: '4820400.0000', payouts: '4612600.0000', grossGamingRevenue: '207800.0000', realisedHoldPct: '4.31', configuredEdgePct: '3.00', betCount: 38412, playerCount: 1204 },
  { slug: 'dragon-tiger', name: 'Dragon vs Tiger', enabled: true, kind: 'round', turnover: '2140900.0000', payouts: '2094100.0000', grossGamingRevenue: '46800.0000', realisedHoldPct: '2.19', configuredEdgePct: '2.16', betCount: 21044, playerCount: 902 },
  { slug: 'mines', name: 'Mines', enabled: true, kind: 'instant', turnover: '1806200.0000', payouts: '1790700.0000', grossGamingRevenue: '15500.0000', realisedHoldPct: '0.86', configuredEdgePct: '2.50', betCount: 44190, playerCount: 688 },
  { slug: 'andar-bahar', name: 'Andar Bahar', enabled: true, kind: 'round', turnover: '1420800.0000', payouts: '1386100.0000', grossGamingRevenue: '34700.0000', realisedHoldPct: '2.44', configuredEdgePct: '2.46', betCount: 16820, playerCount: 741 },
  { slug: 'plinko', name: 'Plinko', enabled: true, kind: 'instant', turnover: '980400.0000', payouts: '961800.0000', grossGamingRevenue: '18600.0000', realisedHoldPct: '1.90', configuredEdgePct: '2.00', betCount: 29011, playerCount: 512 },
  { slug: 'european-roulette', name: 'European Roulette', enabled: true, kind: 'table', turnover: '640200.0000', payouts: '622900.0000', grossGamingRevenue: '17300.0000', realisedHoldPct: '2.70', configuredEdgePct: '2.70', betCount: 8140, playerCount: 288 },
  { slug: 'keno', name: 'Keno', enabled: true, kind: 'round', turnover: '210500.0000', payouts: '201900.0000', grossGamingRevenue: '8600.0000', realisedHoldPct: '4.09', configuredEdgePct: '4.00', betCount: 5120, playerCount: 194 },
  { slug: 'teen-patti', name: 'Teen Patti (dealer)', enabled: false, kind: 'table', turnover: '0.0000', payouts: '0.0000', grossGamingRevenue: '0.0000', realisedHoldPct: null, configuredEdgePct: '2.25', betCount: 0, playerCount: 0 },
];

export function overviewFor(window: MetricsWindow): Overview {
  const scale: Record<MetricsWindow, number> = { '24h': 1, '7d': 6, '30d': 24 };
  const factor = scale[window];
  const times = (amount: string): string => {
    const [whole = '0', frac = '0000'] = amount.split('.');
    const units = BigInt(whole + frac.padEnd(4, '0')) * BigInt(factor);
    const text = units.toString().padStart(5, '0');
    return `${text.slice(0, -4)}.${text.slice(-4)}`;
  };
  const hours: Record<MetricsWindow, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 };

  return {
    windowFrom: iso(hours[window] * 60),
    windowTo: iso(0),
    turnover: times('12019400.0000'),
    grossGamingRevenue: times('349300.0000'),
    payouts: times('11670100.0000'),
    realisedHoldPct: '2.91',
    configuredEdgePct: '2.63',
    betCount: 162737 * factor,
    activePlayers: window === '24h' ? 1846 : window === '7d' ? 5921 : 14204,
    newPlayers: window === '24h' ? 214 : window === '7d' ? 1108 : 3860,
    depositsTotal: times('1840000.0000'),
    withdrawalsTotal: times('1204000.0000'),
    pendingDeposits: 4,
    pendingWithdrawals: 3,
    pendingWithdrawalAmount: '62000.0000',
  };
}

// ----------------------------------------------------------- payment methods

export const paymentMethods: AdminPaymentMethod[] = [
  { id: 1, kind: 'upi', currency: 'INR', chain: null, asset: null, region: 'IN', label: 'UPI · HDFC 9021', ownerEntity: 'Northwind Retail Pvt Ltd', addressMasked: 'north…9021@hdfcbank', displayName: 'Northwind Retail', enabled: true, health: 'healthy', weight: 120, dailyLimit: '500000.0000', feePct: '2.0000', feeFixed: '3.0000', usedToday: '486200.0000', usageDate: iso(0), depositsToday: 214 },
  { id: 2, kind: 'upi', currency: 'INR', chain: null, asset: null, region: 'IN', label: 'UPI · SBI 3390', ownerEntity: 'Northwind Retail Pvt Ltd', addressMasked: 'north…3390@sbi', displayName: 'Northwind Retail', enabled: true, health: 'degraded', weight: 60, dailyLimit: '300000.0000', feePct: '2.0000', feeFixed: '3.0000', usedToday: '41000.0000', usageDate: iso(0), depositsToday: 22 },
  { id: 3, kind: 'upi', currency: 'INR', chain: null, asset: null, region: 'IN', label: 'UPI · Axis 4412', ownerEntity: 'Northwind Retail Pvt Ltd', addressMasked: 'north…4412@axisbank', displayName: 'Northwind Retail', enabled: true, health: 'healthy', weight: 100, dailyLimit: '500000.0000', feePct: '2.0000', feeFixed: '3.0000', usedToday: '212400.0000', usageDate: iso(0), depositsToday: 118 },
  { id: 4, kind: 'bank', currency: 'INR', chain: null, asset: null, region: 'IN', label: 'Bank · ICICI 7730', ownerEntity: 'Northwind Retail Pvt Ltd', addressMasked: 'ICIC0000773 / ••••4526', displayName: null, enabled: true, health: 'healthy', weight: 80, dailyLimit: '1000000.0000', feePct: '0.5000', feeFixed: '12.0000', usedToday: '318000.0000', usageDate: iso(0), depositsToday: 41 },
  { id: 5, kind: 'crypto', currency: 'USDT', chain: 'TRON', asset: 'USDT-TRC20', region: 'GLOBAL', label: 'Crypto · USDT-TRC20', ownerEntity: 'Northwind Digital FZ-LLC', addressMasked: 'xpub…9K2t (derived per deposit)', displayName: 'Northwind Digital', enabled: true, health: 'healthy', weight: 100, dailyLimit: '2000000.0000', feePct: '0.1000', feeFixed: '1.0000', usedToday: '640000.0000', usageDate: iso(0), depositsToday: 33 },
  { id: 6, kind: 'upi', currency: 'INR', chain: null, asset: null, region: 'IN', label: 'UPI · Kotak 1180', ownerEntity: 'Northwind Retail Pvt Ltd', addressMasked: 'north…1180@kotak', displayName: 'Northwind Retail', enabled: false, health: 'down', weight: 100, dailyLimit: '400000.0000', feePct: '2.0000', feeFixed: '3.0000', usedToday: '0.0000', usageDate: iso(0), depositsToday: 0 },
];

/**
 * A COPY of what the server derives from `payments/chains.ts` and
 * `payments/regions.ts`, for mock mode only. It is a fixture, not a source: the
 * live panel renders whatever the server sends with this endpoint and never
 * consults this constant. Kept faithful to the real tables so a screen driven
 * in mock mode is not lying about which asset auto-credits.
 */
export const paymentMethodOptions: PaymentMethodOptions = {
  kinds: [
    { id: 'upi', label: 'UPI', needsChain: false },
    { id: 'bank', label: 'Bank transfer', needsChain: false },
    { id: 'crypto', label: 'Crypto (address derived per deposit)', needsChain: true },
    { id: 'wallet', label: 'Exchange wallet (one address, memo matched)', needsChain: true },
  ],
  currencies: ['INR', 'USDT', 'USD', 'EUR', 'BRL'],
  regions: [
    { id: 'IN', currencies: ['INR'] },
    { id: 'EU', currencies: ['EUR'] },
    { id: 'US', currencies: ['USD'] },
    { id: 'BR', currencies: ['BRL'] },
    { id: 'CA', currencies: ['USD', 'USDT'] },
    { id: 'GLOBAL', currencies: ['USDT', 'USD', 'EUR'] },
  ],
  chains: [
    {
      id: 'BTC',
      label: 'Bitcoin',
      confirmations: 3,
      assets: [
        {
          id: 'BTC',
          autoCreditCurrency: null,
          warning:
            'BTC does not auto-credit: an operator must confirm every deposit with an fx rate. Amounts are stored at four decimal places (B-14), so a BTC quantity cannot be recorded exactly — only the credited currency amount is exact.',
        },
      ],
    },
    {
      id: 'EVM',
      label: 'Ethereum / EVM',
      confirmations: 12,
      assets: [
        { id: 'ETH', autoCreditCurrency: null, warning: 'ETH does not auto-credit: it has no one-for-one currency, so an operator must confirm every deposit by hand with an fx rate.' },
        { id: 'USDT-ERC20', autoCreditCurrency: 'USDT', warning: null },
        { id: 'USDC', autoCreditCurrency: 'USD', warning: null },
        { id: 'BNB', autoCreditCurrency: null, warning: 'BNB does not auto-credit: it has no one-for-one currency, so an operator must confirm every deposit by hand with an fx rate.' },
        { id: 'MATIC', autoCreditCurrency: null, warning: 'MATIC does not auto-credit: it has no one-for-one currency, so an operator must confirm every deposit by hand with an fx rate.' },
      ],
    },
    {
      id: 'TRON',
      label: 'Tron',
      confirmations: 19,
      assets: [{ id: 'USDT-TRC20', autoCreditCurrency: 'USDT', warning: null }],
    },
  ],
  feeBounds: { maxFeePct: '20.0000', maxFeeFixed: '10000.0000' },
};


// ------------------------------------------------------------------ secrets

/**
 * Shaped to exercise the screen rather than to look tidy: one key served from
 * the store and proven, one required key still on the environment, one required
 * key set NOWHERE (the state that stops a rail silently), and one stored key
 * whose provider could not be reached when it was set — the "stored but
 * unproven" state that must not read as a pass.
 *
 * No value appears here, mocked or otherwise. Not even a fake one: a fixture
 * that looks like a key is how a fixture ends up in a screenshot.
 */
export const secrets: SecretView[] = [
  {
    name: 'CHAIN_PROVIDER_EVM_KEY',
    label: 'EVM explorer API key',
    description:
      'Etherscan-family key for the EVM deposit watcher. Without it the URL builds no provider at all, so EVM deposits never auto-credit — and the chain looks quiet rather than broken (ADR-030).',
    kind: 'chain-key',
    chain: 'EVM',
    required: true,
    source: 'database',
    configured: true,
    last4: '9f2c',
    version: 3,
    fingerprint: 'a41c9e77b2d0f158',
    setBy: 'kamal',
    setAt: iso(60 * 26),
    note: 'rotated after the quota reset',
    validation: 'ok',
    validationDetail: 'EVM answered the probe with this key.',
    versions: 3,
    canValidate: true,
  },
  {
    name: 'CHAIN_PROVIDER_TRON_KEY',
    label: 'TronGrid API key',
    description:
      "Effectively mandatory (ADR-030). TronGrid's keyless tier 429s at about six requests in two seconds, so without a key the USDT-TRC20 rail is rate-limited into silence.",
    kind: 'chain-key',
    chain: 'TRON',
    required: true,
    source: 'unset',
    configured: false,
    last4: null,
    version: null,
    fingerprint: null,
    setBy: null,
    setAt: null,
    note: null,
    validation: null,
    validationDetail: null,
    versions: 0,
    canValidate: true,
  },
  {
    name: 'CRYPTO_XPUB',
    label: 'Bitcoin extended public key (legacy name)',
    description:
      'The original Bitcoin xpub, kept because production has derived against it since T-016.',
    kind: 'xpub',
    chain: 'BTC',
    required: false,
    source: 'environment',
    configured: true,
    last4: 'utZYs',
    version: null,
    fingerprint: null,
    setBy: null,
    setAt: null,
    note: null,
    validation: null,
    validationDetail: null,
    versions: 0,
    canValidate: true,
  },
  {
    name: 'CRYPTO_XPUB_BTC',
    label: 'Bitcoin extended public key',
    description:
      'SLIP-132 xpub/ypub/zpub. A PRIVATE key is refused — this server must never hold spend authority (ADR-021).',
    kind: 'xpub',
    chain: 'BTC',
    required: false,
    source: 'unset',
    configured: false,
    last4: null,
    version: null,
    fingerprint: null,
    setBy: null,
    setAt: null,
    note: null,
    validation: null,
    validationDetail: null,
    versions: 0,
    canValidate: true,
  },
  {
    name: 'CRYPTO_XPUB_EVM',
    label: 'EVM extended public key',
    description: 'Account-level xpub for EVM address derivation.',
    kind: 'xpub',
    chain: 'EVM',
    required: false,
    source: 'database',
    configured: true,
    last4: 'K3hQ',
    version: 1,
    fingerprint: '77b1d0e4aa20cc31',
    setBy: 'kamal',
    setAt: iso(60 * 24 * 9),
    note: 'moved out of the environment',
    validation: 'ok',
    validationDetail: 'Parsed as an EVM evm public key on mainnet.',
    versions: 1,
    canValidate: true,
  },
  {
    name: 'CRYPTO_XPUB_TRON',
    label: 'Tron extended public key',
    description: 'Account-level xpub for Tron address derivation.',
    kind: 'xpub',
    chain: 'TRON',
    required: false,
    source: 'unset',
    configured: false,
    last4: null,
    version: null,
    fingerprint: null,
    setBy: null,
    setAt: null,
    note: null,
    validation: null,
    validationDetail: null,
    versions: 0,
    canValidate: true,
  },
  {
    name: 'OPS_MODEL_API_KEY',
    label: 'Internal analysis provider key',
    description:
      'Reserved for the internal ops layer. Nothing calls it yet, so it can be stored but not exercised.',
    kind: 'opaque',
    chain: null,
    required: false,
    source: 'database',
    configured: true,
    last4: '4b1e',
    version: 2,
    fingerprint: 'ff0132aa9c8d7e64',
    setBy: 'kamal',
    setAt: iso(60 * 5),
    note: 'rotated during an outage',
    validation: 'unavailable',
    validationDetail:
      'The provider could not be reached to check this key. It is stored but unproven; run the check again when the provider is back.',
    versions: 2,
    canValidate: false,
  },
];

// ------------------------------------------------- analytics v2 (ADR-044)
//
// Deterministic, plausible, shaped to exercise the UI: a visible weekend
// bump, one whale far ahead of the field, retention that decays like real
// retention. Live from the day the server routes shipped; these exist only so
// `pnpm dev` renders the screens without a server.

const dayString = (daysAgo: number): string =>
  new Date(now - daysAgo * 86400000).toISOString().slice(0, 10);

export function dailySeriesFor(days: number): DailyMetric[] {
  const out: DailyMetric[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(now - i * 86400000);
    const weekend = date.getUTCDay() === 0 || date.getUTCDay() === 6;
    const wave = 1 + 0.25 * Math.sin(i / 3) + (weekend ? 0.45 : 0);
    const turnover = Math.round(400000 * wave);
    const payouts = Math.round(turnover * 0.972);
    const deposits = Math.round(62000 * wave);
    const withdrawals = Math.round(41000 * wave);
    out.push({
      day: dayString(i),
      turnover: `${turnover}.0000`,
      payouts: `${payouts}.0000`,
      grossGamingRevenue: `${turnover - payouts}.0000`,
      betCount: Math.round(5400 * wave),
      activePlayers: Math.round(610 * wave),
      newPlayers: Math.round(72 * wave),
      depositsTotal: `${deposits}.0000`,
      depositCount: Math.round(210 * wave),
      withdrawalsTotal: `${withdrawals}.0000`,
      withdrawalCount: Math.round(64 * wave),
    });
  }
  return out;
}

export function funnelFor(days: number): DepositFunnel {
  const registered = 72 * days;
  const firstDeposited = Math.round(registered * 0.31);
  const firstBet = Math.round(registered * 0.44);
  return {
    from: dayString(days - 1),
    to: dayString(0),
    registered,
    firstDeposited,
    firstBet,
    depositConversionPct: ((firstDeposited / registered) * 100).toFixed(2),
    betConversionPct: ((firstBet / registered) * 100).toFixed(2),
  };
}

export const cohorts: RetentionCohort[] = Array.from({ length: 8 }, (_, i) => {
  const weeksAgo = 7 - i;
  const monday = new Date(now - weeksAgo * 7 * 86400000);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  const size = 420 + i * 35;
  const active = Array.from({ length: weeksAgo + 1 }, (_, w) =>
    Math.round(size * 0.62 * Math.pow(0.55, w)),
  );
  return { cohortWeek: monday.toISOString().slice(0, 10), size, active };
});

export const playerLifetime: PlayerLifetime[] = [
  { userId: 1042, username: 'rahul_87', status: 'active', currency: 'INR', turnover: '812400.0000', payouts: '689200.0000', grossGamingRevenue: '123200.0000', betCount: 9210, lastBetAt: iso(42) },
  { userId: 1218, username: 'meghana.k', status: 'active', currency: 'INR', turnover: '410800.0000', payouts: '352600.0000', grossGamingRevenue: '58200.0000', betCount: 5104, lastBetAt: iso(180) },
  { userId: 1121, username: 'vj_chennai', status: 'active', currency: 'INR', turnover: '288000.0000', payouts: '241900.0000', grossGamingRevenue: '46100.0000', betCount: 3388, lastBetAt: iso(300) },
  { userId: 1310, username: 'winbig247', status: 'suspended', currency: 'INR', turnover: '196400.0000', payouts: '158100.0000', grossGamingRevenue: '38300.0000', betCount: 2011, lastBetAt: iso(2600) },
  { userId: 1400, username: 'tron.whale', status: 'active', currency: 'USDT', turnover: '18200.0000', payouts: '16900.0000', grossGamingRevenue: '1300.0000', betCount: 240, lastBetAt: iso(66) },
  { userId: 1287, username: 'lucky.leela', status: 'active', currency: 'INR', turnover: '96200.0000', payouts: '99400.0000', grossGamingRevenue: '-3200.0000', betCount: 1422, lastBetAt: iso(12) },
];

// ---------------------------------------------------- audit (ADR-044)

export const auditEntries: AuditEntry[] = [
  { id: 3121, operatorId: 2, operatorUsername: 'ops.anita', action: 'deposit.confirm', targetType: 'deposit', targetId: 9012, detail: { utr: '527715937221', amount: '2500.0000', via: 'operator-token' }, ip: '10.0.4.12', createdAt: iso(18) },
  { id: 3120, operatorId: 1, operatorUsername: 'admin.dev', action: 'game.update', targetType: 'game', targetId: 14, detail: { slug: 'mines', from: { enabled: true }, to: { enabled: false }, reason: 'payout drift under review', via: 'operator-token' }, ip: '10.0.4.2', createdAt: iso(95) },
  { id: 3119, operatorId: 2, operatorUsername: 'ops.anita', action: 'withdrawal.approve', targetType: 'withdrawal', targetId: 5120, detail: { amount: '12000.0000', via: 'operator-token' }, ip: '10.0.4.12', createdAt: iso(150) },
  { id: 3118, operatorId: 1, operatorUsername: 'admin.dev', action: 'player.suspend', targetType: 'user', targetId: 1310, detail: { reason: 'chargeback pattern', from: 'active', to: 'suspended', via: 'operator-token' }, ip: '10.0.4.2', createdAt: iso(400) },
  { id: 3117, operatorId: null, operatorUsername: null, action: 'auth.login_failed', targetType: 'operator', targetId: null, detail: { username: 'admin', via: 'machine' }, ip: '203.0.113.9', createdAt: iso(1300) },
];

// -------------------------------------- UPI reconciliation (ADR-044)

export const reconciliationReport: ReconciliationReport = {
  generatedAt: iso(0),
  bankRows: 3,
  matched: [
    { rowId: 3, importId: 'imp_mock', bankDate: dayString(0), amount: '999.0000', utr: '527715937221', description: 'UPI-RAHUL K-okhdfcbank', importedAt: iso(4), depositId: 9012, userId: 1042, username: 'rahul_87', depositAmount: '999.0000', amountMatches: true, depositStatus: 'pending', claimantCount: 1 },
  ],
  settled: [
    { rowId: 2, importId: 'imp_mock', bankDate: dayString(1), amount: '2500.0000', utr: '527715937100', description: 'UPI-MEGHANA K-okaxis', importedAt: iso(4), depositId: 8998, userId: 1218, username: 'meghana.k', depositAmount: '2500.0000', amountMatches: true, depositStatus: 'credited', claimantCount: 1 },
  ],
  unmatchedBankRows: [
    { rowId: 1, importId: 'imp_mock', bankDate: dayString(1), amount: '123.4500', utr: '527715937305', description: 'UPI/CR/527715937305/STRANGER/oksbi', importedAt: iso(4) },
  ],
  unclaimedDeposits: [
    { depositId: 9020, userId: 1121, username: 'vj_chennai', amount: '450.0000', reference: 'KSM4412AB', claimedUtr: '527715937400', utrClaimedAt: iso(60), createdAt: iso(75) },
  ],
};
